"use client";

import { useEffect } from "react";

const COOKIE_NAME = "pm_attr";
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days
const APP_HOST = "pushmail.dev";

/**
 * First-touch signup attribution capture.
 *
 * On first arrival, if there's no existing attribution cookie AND the visit
 * carries attribution signal (a utm_source param, or an external referrer),
 * writes a host-only `pm_attr` cookie holding the original-touch context. The
 * signup route later reads this cookie to persist signup_source /
 * signup_attribution on the new user.
 *
 * First-touch: once the cookie exists we never overwrite it. Everything is
 * wrapped in try/catch so a tracking failure can never break the page.
 */
export default function AttributionTracker() {
  useEffect(() => {
    try {
      // First-touch: if we already captured attribution, do nothing.
      if (document.cookie.split("; ").some((c) => c.startsWith(`${COOKIE_NAME}=`))) {
        return;
      }

      const url = new URL(window.location.href);
      const params = url.searchParams;
      const utmSource = params.get("utm_source");

      const referrer = document.referrer || "";
      let referrerHost = "";
      if (referrer) {
        try {
          referrerHost = new URL(referrer).hostname;
        } catch {
          referrerHost = "";
        }
      }

      const isExternalReferrer =
        referrerHost !== "" &&
        referrerHost !== APP_HOST &&
        referrerHost !== `www.${APP_HOST}`;

      // Only capture when there's a real attribution signal.
      if (!utmSource && !isExternalReferrer) {
        return;
      }

      const attribution = {
        utm_source: utmSource,
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        referrer: referrer || null,
        landing_page: url.pathname + url.search,
        captured_at: new Date().toISOString(),
      };

      const value = encodeURIComponent(JSON.stringify(attribution));
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      // Host-only cookie (no Domain attribute) — single domain.
      document.cookie = `${COOKIE_NAME}=${value}; max-age=${MAX_AGE_SECONDS}; path=/; SameSite=Lax${secure}`;
    } catch {
      // Never let attribution tracking break the page.
    }
  }, []);

  return null;
}
